import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, MessageSquare, RefreshCw, Heart, User, Users, CalendarPlus } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBookingNotifications } from "@/lib/useBookingNotifications";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BookingAnswers from "@/components/BookingAnswers";
import HealthWarning from "@/components/HealthWarning";
import { guestOnboardAt } from "@/lib/guestOnboard";
import { useAppTz, dayKeyInTz, hmInTz, fmtTime, zonedIsoFromWallTime } from "@/lib/tz";
import { getServerNow } from "@/lib/serverClock";
import SystemClock from "@/components/SystemClock";

const POLL_MS = 20000;

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday start
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

export default function CalendarPanel() {
  const { admin } = useAuth();
  const isAdmin = ["developer", "admin", "manager", "supervisor"].includes(admin?.role);
  // Yacht timezone — same helper the Spa iPad + guest surfaces use.
  // The admin calendar previously rendered booking times with the
  // browser's local timezone (via `new Date().getHours()` and
  // `.toTimeString()`), which drifted 1-3 h from the Spa iPad on any
  // laptop signed in outside the yacht's tz. Adopting `appTz` here
  // keeps every calendar surface synced to the ship's clock.
  const appTz = useAppTz();
  const canManualBook = ["developer", "admin", "manager"].includes(admin?.role)
    || (["supervisor", "spa_ops"].includes(admin?.role) && !!admin?.can_create_manual_bookings);
  const canSeePhotos = admin?.role && admin.role !== "developer";
  const notify = useBookingNotifications({ appName: "Yacht Spa" });
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [bookings, setBookings] = useState([]);
  const [crew, setCrew] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Manual booking dialog
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualGuests, setManualGuests] = useState([]);
  const [manualTreats, setManualTreats] = useState([]);
  const [manualForm, setManualForm] = useState({ guest_id: "", treatment_id: "", start_date: "", start_time: "", duration_min: null, crew_id: "", notes: "", override_clash: false });
  const [manualClashPrompt, setManualClashPrompt] = useState(null); // { reasons: [...] }
  const latestCreatedAt = useRef(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    const start = new Date(weekStart).toISOString();
    const end = new Date(weekStart); end.setDate(end.getDate() + 7);
    try {
      const { data } = await api.get("/admin/bookings", { params: { start, end: end.toISOString() } });
      setBookings(data || []);
      if (data && data.length) {
        latestCreatedAt.current = data.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current || "");
      }
    } catch (e) {
      if (!silent) toast.error(formatErr(e.response?.data?.detail) || "Could not load");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const pollNew = async () => {
    if (!latestCreatedAt.current) return;
    try {
      const { data } = await api.get("/admin/bookings", { params: { since: latestCreatedAt.current, order: "asc", sort: "created_at" } });
      if (data && data.length) {
        data.forEach((b) => {
          toast.success(`New booking · ${b.guest_name}`, {
            description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`,
            duration: 8000,
          });
          notify(b);
        });
        latestCreatedAt.current = data.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current);
        await load(true);
      }
    } catch (e) { console.debug("[Calendar] booking-notification poll failed:", e); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weekStart]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/crew"); setCrew(data || []); } catch (e) { console.debug("[Calendar] crew load failed:", e); } })(); }, []);
  // Preload guests + treatments once for the manual booking dialog (Manager+ / authorised Supervisor)
  useEffect(() => {
    if (!canManualBook) return;
    (async () => {
      try {
        const [tr, ch] = await Promise.all([api.get("/public/treatments"), api.get("/admin/charters")]);
        setManualTreats(tr.data || []);
        // NB: don't pre-filter charters by "arrival <= now <= departure" — the
        // manual-booking picker filters the guests against the slot's own
        // date/time (see `eligibleManualGuests`). This keeps the roster
        // available when scheduling a booking on a future or past charter.
        const rows = [];
        for (const c of (ch.data || [])) {
          for (const g of (c.guests || [])) {
            if (g.guest_id) rows.push({
              ...g,
              charter_name: c.name,
              charter_arrival: c.arrival,
              charter_departure: c.departure,
            });
          }
        }
        setManualGuests(rows);
      } catch (e) { console.debug("[Calendar] manual-booking preload failed:", e); }
    })();
  }, [canManualBook]);

  // Guest filter — only guests actually onboard at the picked date + time
  // are eligible. Mirrors the backend guard so the dropdown and the
  // backend agree.
  const eligibleManualGuests = useMemo(() => {
    if (!manualForm.start_date || !manualForm.start_time) return manualGuests;
    const iso = new Date(`${manualForm.start_date}T${manualForm.start_time}:00`).toISOString();
    return manualGuests.filter((g) => guestOnboardAt(g, iso));
  }, [manualGuests, manualForm.start_date, manualForm.start_time]);

  // If the operator changes the date after picking a guest and the new
  // slot is outside that guest's stay window, clear the selection.
  useEffect(() => {
    if (!manualForm.guest_id) return;
    if (!eligibleManualGuests.some((g) => g.guest_id === manualForm.guest_id)) {
      setManualForm((f) => ({ ...f, guest_id: "" }));
    }
  }, [eligibleManualGuests, manualForm.guest_id]);

  const submitManual = async () => {
    if (!manualForm.guest_id || !manualForm.treatment_id || !manualForm.start_date || !manualForm.start_time) {
      toast.error("Guest, treatment, date & time are required"); return;
    }
    setManualBusy(true);
    try {
      const iso = new Date(`${manualForm.start_date}T${manualForm.start_time}:00`).toISOString();
      await api.post("/admin/bookings/manual", {
        guest_id: manualForm.guest_id,
        treatment_id: manualForm.treatment_id,
        start_at: iso,
        duration_min: manualForm.duration_min || null,
        crew_id: manualForm.crew_id || null,
        notes: manualForm.notes || "",
        override_clash: !!manualForm.override_clash,
      });
      toast.success(manualForm.override_clash ? "Booking created with override" : "Manual booking created");
      setManualOpen(false);
      setManualClashPrompt(null);
      setManualForm({ guest_id: "", treatment_id: "", start_date: "", start_time: "", duration_min: null, crew_id: "", notes: "", override_clash: false });
      load(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured 409 → surface the conflict list + reveal the override checkbox.
      if (e.response?.status === 409 && detail && typeof detail === "object" && Array.isArray(detail.clash_reasons)) {
        setManualClashPrompt({ reasons: detail.clash_reasons, message: detail.message, canOverride: detail.can_override !== false });
      } else {
        toast.error(formatErr(detail) || "Could not create");
      }
    }
    finally { setManualBusy(false); }
  };
  useEffect(() => {
    const t = setInterval(pollNew, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Real-time WebSocket wiring — instant refresh on any booking.* event.
  useLiveEvents(useCallback((ev) => {
    if (!ev || !ev.type || !ev.type.startsWith("booking.")) return;
    const b = ev.booking || {};
    if (ev.type === "booking.created") { notify(b); toast.success(`New booking · ${b.guest_name}`, { description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`, duration: 8000 }); }
    else if (ev.type === "booking.cancelled") toast.warning(`Cancelled · ${b.guest_name}`);
    else if (ev.type === "booking.deleted") toast(`Removed · ${b.guest_name}`);
    load(true);
  }, [notify]));

  const hours = Array.from({ length: 13 }, (_, i) => 8 + i); // 8..20

  // Group couples bookings into one composite tile. Two docs share `couple_booking_id`.
  const composites = useMemo(() => {
    const byCouple = new Map();
    const singles = [];
    for (const b of bookings) {
      if (b.couple_booking_id) {
        const arr = byCouple.get(b.couple_booking_id) || [];
        arr.push(b);
        byCouple.set(b.couple_booking_id, arr);
      } else {
        singles.push({ id: b.id, ...b, isCouple: false, primary: b, partner: null });
      }
    }
    const couples = [];
    for (const [cid, arr] of byCouple.entries()) {
      // Sort so the earliest-created is primary (stable ordering).
      arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      const primary = arr[0];
      const partner = arr[1] || null;
      couples.push({
        id: primary.id,
        couple_booking_id: cid,
        isCouple: !!partner,
        isFriendDual: !!partner && (primary.partner_kind === "friend" || partner.partner_kind === "friend"),
        isPending: !!partner && (primary.invite_status === "pending" || partner.invite_status === "pending"),
        primary,
        partner,
        start_at: primary.start_at,
        end_at: primary.end_at,
        treatment_name: primary.treatment_name,
        duration_min: primary.duration_min,
        status: primary.status,
        crew_id: primary.crew_id,
        guest_name: partner ? `${primary.guest_name} & ${partner.guest_name}` : primary.guest_name,
        notes: [primary.notes, partner?.notes].filter(Boolean).join(" · "),
        health_snapshot: primary.health_snapshot,
      });
    }
    return [...singles, ...couples];
  }, [bookings]);

  const bookingsFor = (day, hour) => composites.filter((b) => {
    // Compare in the yacht timezone — a booking at 08:00 EEST must land
    // in the 08:00 row on every laptop, not shift by the viewer's UTC
    // offset. `hmInTz` returns hours in the target tz; day comparison
    // uses `dayKeyInTz` (YYYY-MM-DD in that tz) for the same reason.
    if (!b.start_at) return false;
    const bookingDay = dayKeyInTz(b.start_at, appTz);
    const cellDay = dayKeyInTz(day.toISOString(), appTz);
    if (bookingDay !== cellDay) return false;
    const { hour: bh } = hmInTz(b.start_at, appTz);
    return bh === hour;
  });

  const update = async (item, patch) => {
    try {
      const ids = item.isCouple ? [item.primary.id, item.partner.id] : [item.primary?.id || item.id];
      for (const id of ids) {
        await api.put(`/admin/bookings/${id}`, patch);
      }
      toast.success(item.isCouple ? "Couple booking updated" : "Updated");
      setSelected(null); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Could not update booking"); }
  };

  const remove = async (item) => {
    const label = item.isCouple ? "this couple booking (both guests)" : "this booking";
    if (!window.confirm(`Delete ${label}?`)) return;
    const ids = item.isCouple ? [item.primary.id, item.partner.id] : [item.primary?.id || item.id];
    for (const id of ids) {
      await api.delete(`/admin/bookings/${id}`);
    }
    setSelected(null); load();
  };

  const shift = (days) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + days); setWeekStart(d);
  };

  const weekLabel = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div>
      {/* Header — collapses to a single compact row on mobile so the actual
          calendar grid gets meaningful vertical space on phones. The
          section title stays visible on ≥md; on mobile the AdminDashboard
          top-bar already labels the current tab as "Calendar" so we don't
          duplicate it here. */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4 md:mb-6">
        <div className="hidden md:block">
          <h2 className="font-display text-3xl">Bookings calendar</h2>
          <p className="text-sm text-muted-foreground mt-1">Week overview · click a booking for details {refreshing && <RefreshCw className="inline h-3 w-3 animate-spin ml-2" />}</p>
        </div>
        {/* Live yacht-clock badge — every scheduling calc reads from here,
            not the device clock. Warns the operator inline when their
            device is out of sync with the ship. */}
        <SystemClock className="hidden md:inline-flex mr-auto ml-4" />
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
          <SystemClock className="md:hidden" compact />
          {canManualBook && (
            <Button
              onClick={() => setManualOpen(true)}
              className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-9 px-3 md:px-4 text-xs md:text-sm"
              data-testid="manual-booking-btn"
            ><CalendarPlus className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Manual booking</span></Button>
          )}
          <Button variant="outline" size="icon" onClick={() => shift(-7)} data-testid="cal-prev" className="h-9 w-9"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-display text-sm md:text-lg px-2 md:px-3 whitespace-nowrap" data-testid="cal-week-label">{weekLabel}</div>
          <Button variant="outline" size="icon" onClick={() => shift(7)} data-testid="cal-next" className="h-9 w-9"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setWeekStart(startOfWeek(getServerNow()))} data-testid="cal-today" className="h-9 px-3 text-xs md:text-sm">Today</Button>
          <Button variant="outline" size="icon" onClick={() => load()} data-testid="cal-refresh" className="h-9 w-9"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-border">
            <div />
            {days.map((d) => (
              // Use the ISO date as a stable key so React reuses the same DOM
              // cell across week navigation instead of remounting.
              <div key={d.toISOString().slice(0, 10)} className="p-3 text-center border-l border-border">
                <div className="overline text-primary/50">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                <div className="font-display text-lg">{d.getDate()}</div>
              </div>
            ))}
          </div>
          {hours.map((h) => (
            <div key={h} className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-border/60 min-h-[64px]">
              <div className="p-2 text-xs text-muted-foreground border-r border-border">{String(h).padStart(2, "0")}:00</div>
              {days.map((d) => {
                const items = bookingsFor(d, h);
                // Drag-and-drop reschedule (Manager+ only) — the drop target is
                // this hour cell; the browser handles the drag preview and we
                // just move the booking's start_at to this day/hour combo.
                const onCellDragOver = (e) => { if (isAdmin) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } };
                const onCellDrop = async (e) => {
                  if (!isAdmin) return;
                  e.preventDefault();
                  const payload = e.dataTransfer.getData("text/plain");
                  if (!payload) return;
                  let dragged;
                  try { dragged = JSON.parse(payload); } catch { return; }
                  // Build the target ISO in the YACHT TIMEZONE — the calendar
                  // was rendered in `appTz`, so "hour h on day d" refers to
                  // yacht-tz wall clock, not the operator's browser tz. Using
                  // `new Date(d).setHours(h)` before this fix produced a
                  // browser-local time — an admin on UTC saw the booking
                  // "jump" +3 h to 13:00 EEST after dropping on the 10:00 row.
                  const targetIso = zonedIsoFromWallTime(d, h, dragged.minute || 0, appTz);
                  if (targetIso === dragged.start_at) return;
                  try {
                    const ids = dragged.isCouple && dragged.partnerId ? [dragged.id, dragged.partnerId] : [dragged.id];
                    for (const id of ids) await api.put(`/admin/bookings/${id}`, { start_at: targetIso });
                    toast.success(dragged.isCouple ? "Couple rescheduled" : "Rescheduled");
                    load();
                  } catch (err) {
                    toast.error(formatErr(err.response?.data?.detail) || "Reschedule failed");
                  }
                };
                return (
                  <div
                    key={`${d.toISOString().slice(0, 10)}-${h}`}
                    className="border-l border-border/60 p-1 space-y-1 relative group"
                    onDragOver={onCellDragOver}
                    onDrop={onCellDrop}
                    data-testid={`cal-cell-${d.toISOString().slice(0,10)}-${h}`}
                  >
                    {items.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => setSelected(b)}
                        draggable={isAdmin && b.status !== "cancelled"}
                        onDragStart={(e) => {
                          if (!isAdmin || b.status === "cancelled") { e.preventDefault(); return; }
                          const s = new Date(b.start_at);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", JSON.stringify({
                            id: b.primary?.id || b.id,
                            partnerId: b.partner?.id || null,
                            isCouple: !!b.isCouple,
                            start_at: s.toISOString(),
                            minute: s.getMinutes(),
                          }));
                        }}
                        title={b.clash_override ? `Manager override · ${(b.clash_reasons||[]).join(" · ")}` : (isAdmin && b.status !== "cancelled" ? "Drag to another day/hour cell to reschedule" : undefined)}
                        className={`w-full text-left px-2 py-1 text-xs border relative ${isAdmin && b.status !== "cancelled" ? "cursor-grab active:cursor-grabbing" : ""} ${
                          b.status === "cancelled" ? "bg-muted text-muted-foreground line-through"
                          : b.isPending ? "bg-emerald-500/25 text-emerald-800 dark:text-emerald-200 border-dashed border-emerald-600 hover:bg-emerald-500/35"
                          : b.isFriendDual ? "bg-emerald-500/95 text-white border-emerald-600 hover:bg-emerald-500"
                          : b.isCouple ? "bg-accent/90 text-primary border-accent hover:bg-accent"
                          : "bg-primary/95 text-primary-foreground border-primary hover:bg-primary"} ${
                          b.clash_override ? "ring-2 ring-amber-400 ring-offset-1 ring-offset-background" : ""
                        }`}
                        data-testid={`booking-${b.id}`}
                      >
                        {b.clash_override && (
                          <div className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[9px] px-1 py-0.5 uppercase tracking-wider font-medium" data-testid={`booking-override-tag-${b.id}`} title="Manager override — this slot has conflicts">
                            !
                          </div>
                        )}
                        <div className="font-medium truncate flex items-center gap-1">
                          {b.isFriendDual && <Users className="h-3 w-3 shrink-0" strokeWidth={2.5} data-testid={`booking-friend-icon-${b.id}`} />}
                          {b.isCouple && !b.isFriendDual && <Heart className="h-3 w-3 shrink-0" strokeWidth={2.5} data-testid={`booking-couple-icon-${b.id}`} />}
                          <HealthWarning
                            size="xs"
                            testId={`booking-health-warn-${b.id}`}
                            guests={[
                              { name: b.primary?.guest_name, snapshot: b.primary?.health_snapshot },
                              { name: b.partner?.guest_name, snapshot: b.partner?.health_snapshot },
                            ]}
                          />
                          <span className="truncate">{fmtTime(b.start_at, appTz)} · {b.guest_name}</span>
                        </div>
                        <div className="truncate opacity-80">{b.treatment_name}{b.isPending ? " · pending" : b.isFriendDual ? " · friend dual" : b.isCouple ? " · couples" : ""}</div>
                        {b.notes && (
                          <MessageSquare
                            className="h-3 w-3 text-primary absolute top-1 right-1"
                            strokeWidth={2.5}
                            data-testid={`booking-note-icon-${b.id}`}
                          />
                        )}
                      </button>
                    ))}
                    {canManualBook && items.length === 0 && (
                      <button
                        onClick={() => {
                          const yyyy = d.getFullYear();
                          const mm = String(d.getMonth() + 1).padStart(2, "0");
                          const dd = String(d.getDate()).padStart(2, "0");
                          const hh = String(h).padStart(2, "0");
                          setManualForm((f) => ({ ...f, start_date: `${yyyy}-${mm}-${dd}`, start_time: `${hh}:00` }));
                          setManualOpen(true);
                        }}
                        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-accent/70 hover:text-accent hover:bg-accent/5 text-2xl font-light"
                        data-testid={`quick-book-${d.toISOString().slice(0,10)}-${h}`}
                        title={`Manual booking · ${d.toLocaleDateString()} ${String(h).padStart(2,"0")}:00`}
                      >+</button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              {selected?.isCouple && <Heart className="h-5 w-5 text-accent" strokeWidth={2} />}
              {selected?.isCouple ? "Couples booking" : "Booking details"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="overline text-primary/50">Treatment</div><div>{selected.treatment_name}</div>
                <div className="overline text-primary/50">When</div><div>{fmtTime(selected.start_at, appTz)} · {new Date(selected.start_at).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: appTz })}</div>
                <div className="overline text-primary/50">Duration</div><div>{selected.duration_min} min</div>
                <div className="overline text-primary/50">Therapist</div><div data-testid="cal-therapist">{crew.find((c) => c.id === selected.crew_id)?.name || "Any"}</div>
                <div className="overline text-primary/50">Status</div><div className="capitalize">{selected.status}</div>
              </div>

              {/* Per-guest details (2 blocks when couple, 1 otherwise) */}
              {[selected.primary, selected.partner].filter(Boolean).map((g, idx) => (
                <div key={g.id} className={`border ${selected.isCouple ? "border-accent/40 bg-accent/5" : "border-border bg-muted/40"} p-4 space-y-2`} data-testid={`booking-guest-${idx}`}>
                  <div className="flex items-center gap-3">
                    {canSeePhotos && (
                      g.guest_image_path ? (
                        <img
                          src={fileUrl(g.guest_image_path)}
                          alt={g.guest_name}
                          className="w-14 h-14 rounded-full object-cover border border-border"
                          data-testid={`cal-detail-avatar-${idx}`}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-muted border border-border flex items-center justify-center">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="overline text-primary/60 flex items-center gap-1">
                        {selected.isCouple && (idx === 0 ? "Guest A" : "Guest B")}
                        {!selected.isCouple && "Guest"}
                      </div>
                      <div className="font-display text-lg truncate flex items-center gap-1.5">
                        {g.guest_name}
                        <HealthWarning
                          size="md"
                          testId={`booking-detail-health-warn-${idx}`}
                          guests={[{ snapshot: g.health_snapshot }]}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {g.guest_email && (<>
                      <div className="text-muted-foreground">Email</div><div className="truncate">{g.guest_email}</div>
                    </>)}
                  </div>
                  {g.notes && (
                    <div>
                      <div className="overline text-accent mt-2 mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Special request</div>
                      <div className="bg-background border-l-2 border-accent p-2 text-xs whitespace-pre-wrap" data-testid={`calendar-notes-${idx}`}>{g.notes}</div>
                    </div>
                  )}
                  <BookingAnswers answers={g.answers} compact />
                </div>
              ))}

              {isAdmin ? (
                <>
                  <div>
                    <Label className="overline text-primary/60">Reschedule to</Label>
                    <Input
                      type="datetime-local"
                      defaultValue={new Date(new Date(selected.start_at).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0,16)}
                      onChange={(e) => setSelected({ ...selected, _newStart: e.target.value })}
                      className="mt-2"
                      data-testid="reschedule-input"
                    />
                    {selected.isCouple && <p className="text-xs text-muted-foreground mt-1">Both guests will be moved together.</p>}
                  </div>

                  <div>
                    <Label className="overline text-primary/60">Assign crew</Label>
                    <Select value={selected.crew_id || "none"} onValueChange={(v) => setSelected({ ...selected, crew_id: v === "none" ? null : v })}>
                      <SelectTrigger className="mt-2" data-testid="assign-crew"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Any</SelectItem>
                        {crew.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {selected.isCouple && <p className="text-xs text-muted-foreground mt-1">Assigned to both guests.</p>}
                  </div>

                  <div className="flex justify-between items-center pt-4">
                    <Button variant="outline" onClick={() => remove(selected)} className="text-destructive border-destructive/40" data-testid="delete-booking-btn">
                      <X className="h-3 w-3 mr-1" /> Delete{selected.isCouple ? " both" : ""}
                    </Button>
                    <div className="flex gap-2">
                      {selected.status !== "cancelled" && (
                        <Button variant="outline" onClick={() => update(selected, { status: "cancelled" })} data-testid="cancel-booking-btn">
                          Cancel{selected.isCouple ? " both" : ""}
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          const patch = { crew_id: selected.crew_id || null };
                          if (selected._newStart) patch.start_at = new Date(selected._newStart).toISOString();
                          update(selected, patch);
                        }}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="save-booking-btn"
                      >Save</Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="pt-4">
                  <div className="text-xs text-muted-foreground border-t border-border pt-4" data-testid="crew-readonly-note">
                    Read-only view. Ask an admin to reschedule, reassign or cancel.
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual booking dialog — Manager+ or authorised Supervisor / Spa Ops */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="manual-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-accent" /> Manual booking
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground mb-4">
            Record a walk-in or verbally-requested appointment on behalf of a guest.
            The guest will see it in their reservations exactly as if they'd booked it themselves.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="overline text-primary/60 text-[10px]">Guest</Label>
              <Select value={manualForm.guest_id} onValueChange={(v) => setManualForm((f) => ({ ...f, guest_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="mb-guest">
                  <SelectValue placeholder={manualForm.start_date && manualForm.start_time ? "Pick a guest onboard at this slot…" : "Pick date & time first, then a guest…"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {eligibleManualGuests.map((g) => (
                    <SelectItem key={g.guest_id} value={g.guest_id}>
                      {g.name}{g.cabin_name ? ` · ${g.cabin_name}` : ""} · {g.charter_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {manualForm.start_date && manualForm.start_time && manualGuests.length > 0 && eligibleManualGuests.length === 0 && (
                <p className="text-[11px] text-amber-700 dark:text-amber-500 mt-1" data-testid="mb-no-eligible-guests">
                  No guests are scheduled to be onboard at this date/time.
                </p>
              )}
              {manualForm.start_date && manualForm.start_time && eligibleManualGuests.length > 0 && eligibleManualGuests.length < manualGuests.length && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Showing {eligibleManualGuests.length} of {manualGuests.length} guests — the rest aren't onboard at this slot.
                </p>
              )}
            </div>
            <div>
              <Label className="overline text-primary/60 text-[10px]">Treatment</Label>
              <Select
                value={manualForm.treatment_id}
                onValueChange={(v) => {
                  // Prefill duration with the treatment's primary length so the
                  // operator sees a valid choice by default and only tweaks if
                  // they want a longer/shorter slot. `durations` items are
                  // {minutes, price, label} objects — extract the minutes only.
                  const t = manualTreats.find((x) => x.id === v);
                  const extra = (t?.durations || []).map((d) => (typeof d === "number" ? d : d?.minutes)).filter(Boolean);
                  const opts = t ? Array.from(new Set([t.duration_min, ...extra].filter(Boolean))) : [];
                  setManualForm((f) => ({ ...f, treatment_id: v, duration_min: opts[0] || null }));
                }}
              >
                <SelectTrigger className="mt-1" data-testid="mb-treatment"><SelectValue placeholder="Pick a treatment…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {manualTreats.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Duration selector — appears once a treatment is picked. Merges
                the treatment's primary `duration_min` with any extra `durations`
                (each stored as {minutes, price, label}). Deduped + sorted so the
                dropdown always looks clean. */}
            {manualForm.treatment_id && (() => {
              const t = manualTreats.find((x) => x.id === manualForm.treatment_id);
              if (!t) return null;
              const extra = (t.durations || []).map((d) => (typeof d === "number" ? d : d?.minutes)).filter(Boolean);
              const opts = Array.from(new Set([t.duration_min, ...extra].filter(Boolean))).sort((a, b) => a - b);
              if (opts.length === 0) return null;
              return (
                <div>
                  <Label className="overline text-primary/60 text-[10px]">Duration</Label>
                  <Select
                    value={String(manualForm.duration_min || opts[0])}
                    onValueChange={(v) => setManualForm((f) => ({ ...f, duration_min: parseInt(v, 10) }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="mb-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {opts.map((d) => (
                        <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {opts.length === 1 && (
                    <p className="text-[10px] text-muted-foreground mt-1 italic">
                      This treatment only offers a single duration.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-primary/60 text-[10px]">Date</Label>
                <Input type="date" value={manualForm.start_date} onChange={(e) => setManualForm((f) => ({ ...f, start_date: e.target.value }))} className="mt-1" data-testid="mb-date" />
              </div>
              <div>
                <Label className="overline text-primary/60 text-[10px]">Time</Label>
                <Input type="time" step="300" value={manualForm.start_time} onChange={(e) => setManualForm((f) => ({ ...f, start_time: e.target.value }))} className="mt-1" data-testid="mb-time" />
              </div>
            </div>
            <div>
              <Label className="overline text-primary/60 text-[10px]">Therapist (optional)</Label>
              <Select value={manualForm.crew_id || "any"} onValueChange={(v) => setManualForm((f) => ({ ...f, crew_id: v === "any" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="mb-crew"><SelectValue placeholder="Any available" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any available</SelectItem>
                  {crew.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-primary/60 text-[10px]">Notes (optional)</Label>
              <Textarea value={manualForm.notes} onChange={(e) => setManualForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Requested by phone / dining room / …" className="mt-1 min-h-[60px]" data-testid="mb-notes" />
            </div>

            {/* Conflict prompt — after a 409 we surface every clash reason and
                let the manager tick "Override" to re-submit. The persisted
                booking then renders with a yellow border on the calendar. */}
            {manualClashPrompt && (
              <div className="border border-amber-500/50 bg-amber-500/5 p-3 space-y-2" data-testid="mb-clash-prompt">
                <div className="text-xs font-medium text-amber-800 dark:text-amber-300">
                  {manualClashPrompt.message || "This slot has conflicts"}
                </div>
                <ul className="text-[11px] text-amber-800 dark:text-amber-300 list-disc pl-4 space-y-0.5">
                  {manualClashPrompt.reasons.map((r, i) => (
                    <li key={i} data-testid={`mb-clash-reason-${i}`}>{r}</li>
                  ))}
                </ul>
                {manualClashPrompt.canOverride !== false && (
                  <label className="flex items-center gap-2 text-xs pt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!manualForm.override_clash}
                      onChange={(e) => setManualForm((f) => ({ ...f, override_clash: e.target.checked }))}
                      className="h-4 w-4 accent-amber-500"
                      data-testid="mb-override-toggle"
                    />
                    <span className="font-medium">Override and book anyway</span>
                    <span className="text-muted-foreground">(shown with yellow border)</span>
                  </label>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setManualOpen(false)} data-testid="mb-cancel">Cancel</Button>
              <Button onClick={submitManual} disabled={manualBusy} className="rounded-full bg-accent hover:bg-accent/90" data-testid="mb-submit">
                {manualBusy ? "Creating…" : "Create booking"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
